import type { AuthProviderDefinition } from '#layers/access/types/auth-providers/define';
import type { WireAuthProviderListEntry } from '#layers/access/utils/auth-providers/wire';
import { useLocalStorage } from '@vueuse/core';
import { computed, readonly, ref } from 'vue';
import { STORAGE_KEYS } from '#layers/access/constants/auth';
import { isMissingEndpointError } from '#layers/access/utils/auth-providers/is-missing-endpoint-error';
import { getAuthProviderForResponse } from '#layers/access/utils/auth-providers/registry';
import { WireAuthProviderListEntrySchema, WireAuthProviderListResponseSchema } from '#layers/access/utils/auth-providers/wire';
import { useSystemFetch } from '#layers/base/public';

export interface LoginProviderEntry extends WireAuthProviderListEntry {
	def: AuthProviderDefinition | undefined;
}

function decorate(entries: readonly WireAuthProviderListEntry[]): LoginProviderEntry[] {
	return entries.map((entry) => ({
		...entry,
		def: getAuthProviderForResponse({ preset: entry.preset, protocol: entry.protocol }),
	}));
}

function asEntries(value: unknown): WireAuthProviderListEntry[] {
	if (!Array.isArray(value)) return [];

	return value.flatMap((entry) => {
		const result = WireAuthProviderListEntrySchema.safeParse(entry);
		return result.success ? [result.data] : [];
	});
}

// Module-scoped so login page + wizard preview share one fetch per lifetime.
// Cached in localStorage so returning visitors skip the spinner on reload.
const rawEntries = useLocalStorage<WireAuthProviderListEntry[]>(STORAGE_KEYS.loginProvidersCache, []);
const isLoading = ref(asEntries(rawEntries.value).length === 0);
const error = ref<unknown>(null);
let initialized = false;
// Cancels older refreshes so stale responses can't clobber fresher ones.
let activeController: AbortController | undefined;

async function refresh(): Promise<void> {
	activeController?.abort();
	const controller = new AbortController();
	activeController = controller;

	// Warm cache keeps the list visible during revalidation; cold cache shows the spinner.
	isLoading.value = asEntries(rawEntries.value).length === 0;
	error.value = null;

	try {
		const systemFetch = useSystemFetch();
		const result = await systemFetch<unknown>('/auth/providers', { signal: controller.signal });
		// A newer refresh has taken over — let it own the state.
		if (activeController !== controller) return;

		const parsed = WireAuthProviderListResponseSchema.safeParse(result);
		rawEntries.value = parsed.success ? asEntries(parsed.data.data) : [];
	}
	catch (error_) {
		if (activeController !== controller) return;

		if (isMissingEndpointError(error_)) {
			rawEntries.value = [];
		}
		else {
			error.value = error_;
		}
	}
	finally {
		if (activeController === controller) {
			isLoading.value = false;
			activeController = undefined;
		}
	}
}

/**
 * Reset module-scoped state. Test-only; `seed` exercises the warm-cache path.
 */
export function __resetLoginProvidersForTests(seed: WireAuthProviderListEntry[] = []) {
	rawEntries.value = seed;
	isLoading.value = asEntries(seed).length === 0;
	error.value = null;
	initialized = false;
	activeController?.abort();
	activeController = undefined;
}

/**
 * Public, unauthenticated query for active providers on the login page. Falls
 * back to an empty list on 404 so the password form still renders.
 */
export function useLoginProviders() {
	if (!initialized) {
		initialized = true;
		refresh();
	}

	return {
		// Computed (not readonly) avoids TS deep-instantiation on the AuthProviderDefinition graph,
		// while still blocking `.value` reassignment.
		entries: computed(() => decorate(asEntries(rawEntries.value))),
		isLoading: readonly(isLoading),
		error: readonly(error),
		refresh,
	};
}
